<template>
  <div class="mypage-ticket-today-confirm-modal-section">
    <div class="mypage-ticket-today-confirm-modal-section__block">
      <p class="mypage-ticket-today-confirm-modal-section__name">{{ ticketType.ticketTypeName }}</p>
      <div class="mypage-ticket-today-confirm-modal-section__table">
        <mypage-ticket-today-table-parts
          :ticketList="purchasedTickets"
          :ownOrganization="ownOrganization"
          :totalCount="
            getTodayTicketTotalCountByTicketType(
              ticketType,
              ownOrganization,
              availableTodayProductNumbers,
            )
          "
        />
      </div>
      <p class="mypage-ticket-today-confirm-modal-section__alert">
        {{ $tc('MypagePage.MypageTicketTodayPage.confirmAlert') }}
      </p>
      <ul class="mypage-ticket-today-confirm-modal-section__buttons">
        <li class="mypage-ticket-today-confirm-modal-section__button">
          <atom-button
            :linkText="$tc('MypagePage.MypageTicketTodayPage.useTicket')"
            @onClick="handleSubmitClicked"
          />
        </li>
        <li class="mypage-ticket-today-confirm-modal-section__button">
          <atom-button
            :linkText="$tc('common.cancel')"
            :colorType="'tertiary'"
            @onClick="handleCancelClicked"
          />
        </li>
      </ul>
      <div class="mypage-ticket-today-confirm-modal-section__time">
        <mypage-ticket-today-current-time-parts />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api'
import { orderBy } from 'lodash'
import AtomButton from '@/components/atoms/AtomButton.vue'
import MypageTicketTodayTableParts from '@/components/MypageTicketTodayPage/MypageTicketTodayPane/parts/MypageTicketTodayTableParts.vue'
import MypageTicketTodayCurrentTimeParts from '@/components/MypageTicketTodayPage/MypageTicketTodayPane/parts/MypageTicketTodayCurrentTimeParts.vue'
import DigitalTicketDataTicketTypeDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataTicketTypeDocument'
import usePurchasedTicketData, {
  UserPurchasedTicketType,
} from '@/components/hook/ticket/usePurchasedTicketData'
import StoreUtil from '@/store/StoreUtil'
import OrganizationDocument from '@/store/stores/collectionModule/documents/organization/OrganizationDocument'

export default defineComponent({
  name: 'MypageTicketTodayConfirmModalSection',
  components: { MypageTicketTodayCurrentTimeParts, MypageTicketTodayTableParts, AtomButton },
  props: {
    /**
     * デジタルチケット 券種情報
     */
    ticketType: {
      type: Object as PropType<DigitalTicketDataTicketTypeDocument>,
      default: () => ({}),
    },
    /**
     * ログインユーザーが購入した商品情報
     */
    purchasedTicketInfo: {
      type: Object as PropType<UserPurchasedTicketType>,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { ownOrganization } = mypagePageStore
    const { getTodayTicketTotalCountByTicketType } = usePurchasedTicketData()

    /**
     * ログインユーザーが購入したチケット
     */
    const purchasedTickets = computed(() =>
      orderBy(props.ticketType.tickets, 'order')?.filter((ticket) =>
        props.purchasedTicketInfo.productNumbers.availableToday.some(
          (productNumber) => productNumber === ticket.productNumber,
        ),
      ),
    )

    const handleSubmitClicked = () => {
      emit('onClickSubmit')
    }

    const handleCancelClicked = () => {
      emit('onClickCancel')
    }

    return {
      // MypagePageStore
      ownOrganization: ownOrganization as Ref<OrganizationDocument | undefined>,
      // usePurchasedTicketData
      getTodayTicketTotalCountByTicketType,
      // MypageTicketTodayConfirmModalSection
      purchasedTickets,
      availableTodayProductNumbers: props.purchasedTicketInfo.productNumbers
        .availableToday as Array<string>,
      handleSubmitClicked,
      handleCancelClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-today-confirm-modal-section {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  width: 100%;
  height: 100%;
  padding: 16px;
  background-color: $black70;

  &__block {
    position: relative;
    width: 100%;
    max-width: 359px;
    padding: 36px 16px 16px;
    margin: auto;
    background-color: #f7f7f7;
    border-radius: 8px;
  }

  &__name {
    font-size: 32px;
    font-weight: 700;
    color: $black;
    letter-spacing: 0.04em;
  }

  &__table {
    margin-top: 24px;
  }

  &__alert {
    margin-top: 24px;
    font-size: 13px;
    font-weight: 500;
    color: #ff2e00;
    letter-spacing: 0.04em;
    line-height: 1.53;
  }

  &__buttons {
    margin-top: 36px;
  }

  &__button {
    + .mypage-ticket-today-safe-modal-section__button {
      margin-top: 8px;
    }

    &-close {
      position: absolute;
      top: 4px;
      right: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;

      &-icon {
        width: 24px;
        height: 24px;
        color: $black50;
      }
    }
  }

  &__time {
    position: absolute;
    top: -60px;
    left: 50%;
    width: 100%;
    transform: translateX(-50%);

    ::v-deep {
      .mypage-ticket-today-current-time-parts__bg {
        display: none;
      }
    }
  }
}
</style>
